import { memo } from 'react';

import { Typography } from '@mui/material';

import { usePreviewDownload } from '@/features/DataHub/modules/Export/hooks';
import type { PreviewConfig } from '@/features/DataHub/modules/Export/types';

import { DataLoader } from '../Shared';
import { SyntaxHighlighter } from './SyntaxHighlighter';

type PreviewContentProps = {
  downloadFile: (() => void) | undefined;
  error: boolean;
  fileContent: string | undefined;
  loading: boolean;
  previewConfig: PreviewConfig | undefined;
  scrambledContent: string;
};

const PreviewContent = memo(function PreviewContent({
  downloadFile,
  error,
  fileContent,
  loading,
  previewConfig,
  scrambledContent
}: PreviewContentProps) {
  const isReady = !loading && !error && !!fileContent;
  usePreviewDownload(downloadFile, isReady);

  if (!previewConfig) return null;

  if (error) {
    return (
      <Typography
        color='error'
        sx={{ mt: 2, fontSize: '14px' }}
      >
        An error occurred while fetching data. Please try again.
      </Typography>
    );
  }

  if (loading) {
    return <DataLoader />;
  }

  switch (previewConfig.type) {
    case 'code':
    case 'sql':
    case 'api':
      return (
        <SyntaxHighlighter
          fileContent={scrambledContent}
          language={previewConfig.language || 'text'}
        />
      );

    case 'simple':
      return (
        <SyntaxHighlighter
          fileContent={fileContent || ''}
          language={previewConfig.language || 'text'}
        />
      );

    default:
      return null;
  }
});

export default PreviewContent;
